/**
 * CameraController Component
 *
 * Internal component that manages camera behavior within the R3F Canvas.
 * Must be used inside Canvas context to access useFrame and useThree.
 *
 * Features:
 * - Resets camera position when switching tabs
 * - Smooth lerp transitions between positions
 * - Maintains OrbitControls target at origin
 */

import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import { useCameraReset } from './hooks/useCameraReset';

interface CameraControllerProps {
  controlsRef: React.RefObject<OrbitControlsImpl | null>;
}

/**
 * Internal component to handle camera reset logic
 *
 * This component uses the useCameraReset hook which requires
 * access to R3F context (useFrame, useThree), so it must be
 * rendered inside the Canvas.
 *
 * @param props - Component props
 * @param props.controlsRef - Ref to OrbitControls instance
 */
export function CameraController({ controlsRef }: CameraControllerProps) {
  // Apply camera reset on tab switch with smooth transitions
  useCameraReset(controlsRef, {
    smooth: true,
    lerpFactor: 0.1,
  });

  // This component doesn't render anything visible
  return null;
}
